<template>
  <transition name="toast-fade">
    <div
        v-if="show"
        class="toast"
        :class="'toast--' + type"
        role="status"
        aria-live="polite"
        @mouseenter="pause"
        @mouseleave="resume">
      <md-icon class="toast__icon">{{ icon }}</md-icon>
      <span class="toast__msg">{{ message }}</span>
      <span class="toast__close" role="button" :title="closeLabel" @click="dismiss">&times;</span>
    </div>
  </transition>
</template>

<script>
/**
 * Reusable auto-dismissing toast. Shown via a synced `show` prop (v-model:show or
 * :show.sync). Stays put — never redirects/navigates. Auto-hides after `duration`
 * ms; hovering pauses the timer so it can be read.
 *
 * Props:
 *  - show:     visibility (use .sync — emits `update:show`)
 *  - message:  text to display
 *  - type:     'success' | 'error' | 'info' (drives color + icon)
 *  - duration: auto-dismiss ms (0 disables auto-dismiss)
 */
export default {
  name: 'Toast',
  props: {
    show: { type: Boolean, default: false },
    message: { type: String, default: '' },
    type: { type: String, default: 'success' },
    duration: { type: Number, default: 3000 },
    closeLabel: { type: String, default: 'Close' }
  },
  data() {
    return { timer: null };
  },
  computed: {
    icon() {
      if (this.type === 'error') return 'error_outline';
      if (this.type === 'info') return 'info_outline';
      return 'check_circle';
    }
  },
  watch: {
    show(val) {
      if (val) this.startTimer();
      else this.clearTimer();
    }
  },
  mounted() {
    if (this.show) this.startTimer();
  },
  beforeDestroy() {
    this.clearTimer();
  },
  methods: {
    startTimer() {
      this.clearTimer();
      if (this.duration > 0) {
        this.timer = setTimeout(() => this.dismiss(), this.duration);
      }
    },
    clearTimer() {
      if (this.timer) { clearTimeout(this.timer); this.timer = null; }
    },
    pause() { this.clearTimer(); },
    resume() { if (this.show) this.startTimer(); },
    dismiss() {
      this.clearTimer();
      this.$emit('update:show', false);
    }
  }
};
</script>

<style scoped>
.toast {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 9999;
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  max-width: 420px;
  padding: 0.85rem 1rem 0.85rem 0.9rem;
  border-radius: 12px;
  background: #2c2f36;
  color: #fff;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.22);
  font-size: 0.92rem;
  line-height: 1.3;
}
.toast--success { background: #1f8f4e; }
.toast--error { background: #c0392b; }
.toast--info { background: #343841; }

.toast__icon {
  color: #fff !important;
  font-size: 22px !important;
  flex-shrink: 0;
}
.toast__msg { flex: 1; min-width: 0; font-weight: 600; }
.toast__close {
  flex-shrink: 0;
  margin-left: 0.25rem;
  font-size: 20px;
  line-height: 1;
  opacity: 0.8;
  cursor: pointer;
}
.toast__close:hover { opacity: 1; }

/* Slide + fade from the bottom-right */
.toast-fade-enter-active,
.toast-fade-leave-active { transition: opacity .25s ease, transform .25s ease; }
.toast-fade-enter,
.toast-fade-leave-to { opacity: 0; transform: translateY(12px); }
</style>
